import React, { useState } from 'react'

import { Trans } from 'react-i18next'

import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Checkbox } from '@/shared/components/atoms/Checkbox'

import { BillingCycle, PlanConfig } from '../types'

interface SubscriptionSummaryProps {
	plan: PlanConfig
	billingCycle: BillingCycle
	zonesCount: number
	onContinue: () => void
	isLoading?: boolean
	formId?: string
	buttonText?: string
	isTermsAccepted?: boolean
	onTermsAcceptedChange?: (accepted: boolean) => void
}

export const SubscriptionSummary: React.FC<SubscriptionSummaryProps> = ({
	plan,
	billingCycle,
	zonesCount,
	onContinue,
	isLoading = false,
	formId,
	buttonText,
	isTermsAccepted: externalIsTermsAccepted,
	onTermsAcceptedChange,
}) => {
	const { t, currentLanguage } = useTranslation('common')
	// Use internal state if no external state is provided
	const [internalIsTermsAccepted, setInternalIsTermsAccepted] = useState(false)

	const isTermsAccepted =
		externalIsTermsAccepted !== undefined
			? externalIsTermsAccepted
			: internalIsTermsAccepted

	const handleTermsChange = (checked: boolean) => {
		if (onTermsAcceptedChange) {
			onTermsAcceptedChange(checked)
		} else {
			setInternalIsTermsAccepted(checked)
		}
	}

	const pricePerZone =
		billingCycle === 'annual' ? plan.priceAnnual : plan.priceMonthly
	const originalPrice =
		billingCycle === 'annual' ? plan.priceMonthly : plan.priceMonthly
	const total = pricePerZone * zonesCount
	const originalTotal = originalPrice * zonesCount

	const formatPrice = (amount: number): string => {
		return new Intl.NumberFormat(currentLanguage || 'en', {
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount)
	}

	const annualTotal = total * 12

	return (
		<div className="bg-card border border-border rounded-xl p-6">
			<h3 className="text-xl font-semibold mb-6">
				{t('subscribe.summary.title')}
			</h3>

			{/* Plan details */}
			<div className="space-y-3 mb-6">
				<div className="flex items-start justify-between">
					<div>
						<p className="font-medium">{plan.name}</p>
						<p className="text-sm text-muted-foreground">
							x{zonesCount}{' '}
							{zonesCount === 1 ? t('subscribe.zone') : t('subscribe.zones')}
						</p>
					</div>
					<div className="text-right">
						<p className="font-semibold">
							{formatPrice(pricePerZone)} {plan.currency}
							<span className="text-sm font-normal text-muted-foreground">
								/{t('subscribe.zone')}
							</span>
						</p>
						{billingCycle === 'annual' && originalPrice > pricePerZone && (
							<p className="text-sm text-muted-foreground line-through">
								{formatPrice(originalPrice)} {plan.currency}
							</p>
						)}
					</div>
				</div>
			</div>

			{/* Total */}
			<div className="border-t border-border pt-4 mb-4">
				<div className="flex items-start justify-between">
					<p className="text-xl font-semibold">
						{t('subscribe.summary.total')}
					</p>
					<div className="text-right">
						<p className="text-xl font-semibold">
							{formatPrice(total)} {plan.currency}
						</p>
						{billingCycle === 'annual' && originalTotal > total && (
							<p className="text-sm text-muted-foreground line-through">
								{formatPrice(originalTotal)} {plan.currency}
							</p>
						)}
					</div>
				</div>
				<p className="text-sm text-muted-foreground mt-1">
					{billingCycle === 'annual'
						? t('subscribe.summary.billed_annually', {
								amount: formatPrice(annualTotal),
								currency: plan.currency,
							})
						: t('subscribe.summary.billed_monthly')}
				</p>
			</div>

			{/* Terms and Conditions */}
			<div className="flex items-center space-x-2 mb-4">
				<Checkbox
					id="terms"
					checked={isTermsAccepted}
					onCheckedChange={(checked) => handleTermsChange(checked as boolean)}
				/>
				<label
					htmlFor="terms"
					className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
				>
					<Trans
						i18nKey="subscribe.summary.terms_agreement"
						t={t}
						components={[
							<a
								key="terms-link"
								href="https://brandtrack.ai/terms-and-conditions"
								target="_blank"
								rel="noreferrer"
								className="text-primary hover:underline"
							>
								terms
							</a>,
						]}
					/>
				</label>
			</div>

			{/* Continue Button */}
			<Button
				className="w-full rounded-full"
				size="lg"
				// If formId is present, we want to submit the form, not just call onContinue
				// The form submission logic will handle the success callback
				onClick={formId ? undefined : onContinue}
				type={formId ? 'submit' : 'button'}
				form={formId}
				disabled={isLoading || plan.isContactUs || !isTermsAccepted}
			>
				{isLoading
					? t('common.loading')
					: (buttonText ?? t('subscribe.continue'))}
			</Button>

			{/* Tax notice */}
			<p className="text-xs text-muted-foreground text-center mt-4">
				{t('subscribe.summary.excluding_tax')}
			</p>
		</div>
	)
}

export default SubscriptionSummary
